import { BannerSectionProps, LanguageContext } from '@dapperlabs/core-fe';
import { getCookie } from 'cookies-next';
import { GetServerSidePropsContext } from 'next';
import { useEffect, useState } from 'react';
import { useSession } from 'src/edge/session';
import Head from 'src/general/components/Head/Head';
import { useLanguage } from 'src/general/hooks/useLanguage';
import { useTranslation } from 'src/lib/translations';
import { HomePageContent } from 'src/modules/HomePageContent';
import {
  getStaticBanners,
  getStaticFeaturedMoments,
} from 'src/modules/HomePageContent/staticContent';

interface FeaturedMomentsData {
  editionIds: string[];
  link: { label: string; url: string };
  title: string;
}

interface ContentI {
  authedBanners: BannerSectionProps[];
  authedFeaturedMoments: FeaturedMomentsData | null;
  unAuthedBanners: BannerSectionProps[];
  unAuthedFeaturedMoments: FeaturedMomentsData | null;
}

interface HomePageProps {
  content: ContentI;
}

const HomePage = ({ content }: HomePageProps) => {
  const {
    unAuthedBanners,
    authedBanners,
    authedFeaturedMoments,
    unAuthedFeaturedMoments,
  } = content;
  const { t } = useTranslation();

  const { state: sessionState } = useSession();
  const [banners, setBanners] = useState<BannerSectionProps[]>([]);
  const [featuredMoments, setFeaturedMoments] =
    useState<FeaturedMomentsData | null>(null);

  useEffect(() => {
    if (sessionState.matches('LOADING')) return;
    setBanners(sessionState.context.auth ? authedBanners : unAuthedBanners);
    setFeaturedMoments(
      sessionState.context.auth
        ? authedFeaturedMoments
        : unAuthedFeaturedMoments,
    );
  }, [
    sessionState,
    authedBanners,
    unAuthedBanners,
    authedFeaturedMoments,
    unAuthedFeaturedMoments,
  ]);

  const { handleLanguage } = useLanguage();
  return (
    <LanguageContext.Provider value={handleLanguage}>
      <Head
        description={t('Head.HomePage.Description')}
        subtitle={t('Head.HomePage.Subtitle')}
        title={t('Head.HomePage.Title')}
      />
      <HomePageContent
        banners={banners}
        featuredMoments={
          featuredMoments
            ? {
                editionIds: featuredMoments.editionIds,
                link: featuredMoments.link,
                title: featuredMoments.title,
              }
            : { editionIds: [], link: { label: '', url: '' }, title: '' }
        }
      />
    </LanguageContext.Provider>
  );
};

export async function getServerSideProps({
  req,
  res,
}: GetServerSidePropsContext) {
  const locale = (getCookie('language', { req, res }) as string) || 'en-US';

  return {
    props: {
      content: {
        authedBanners: getStaticBanners(locale, true),
        authedFeaturedMoments: getStaticFeaturedMoments(locale, true),
        unAuthedBanners: getStaticBanners(locale, false),
        unAuthedFeaturedMoments: getStaticFeaturedMoments(locale, false),
      },
      locale,
    },
  };
}

export default HomePage;
